import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Building2, User, MapPin, Hammer, ChevronDown, ChevronUp, Layers, CheckCircle2,
  Plus, Trash2, Users, Home, HelpCircle, Info, FileText, Palette
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Workflow Templates - maps to backend project templates
export const WORKFLOW_TEMPLATES = [
  { 
    id: 'design_build_remodel', 
    name: 'Design/Build Remodel',
    description: 'Complete workflow from Client Onboarding through Project Closeout',
    phases: 8,
    recommended: true,
    recommendedFor: 'Remodels, additions, occupied renovations'
  },
  { 
    id: 'new_home_design', 
    name: 'New Home Design',
    description: 'Full architectural design for new construction projects',
    phases: 7,
    recommendedFor: 'New custom home construction'
  },
  { 
    id: 'interior_design', 
    name: 'Interior Design',
    description: 'Interior design focused workflow',
    phases: 5,
    recommendedFor: 'Interior-only projects, furnishings, finishes'
  },
  { 
    id: 'design_consulting', 
    name: 'Design Consulting',
    description: 'Consultation and advisory services workflow',
    phases: 3,
    recommendedFor: 'Consultation, advisory, feasibility studies'
  },
  { 
    id: 'new_home_design_build', 
    name: 'New Home Design/Build',
    description: 'Design and build combined workflow',
    phases: 1,
    stub: true
  }
];

// These will eventually come from backend/admin settings
const DEFAULT_PROJECT_TYPES = [
  'Renovation',
  'New Construction', 
  'Addition',
  'Kitchen Remodel',
  'Bath Remodel',
  'Whole Home Renovation',
  'Commercial Buildout'
];

const DEFAULT_DESIGN_STYLES = [
  'Modern',
  'Contemporary',
  'Traditional',
  'Transitional',
  'Farmhouse',
  'Industrial',
  'Mid-Century Modern',
  'Coastal',
  'Mediterranean',
  'Scandinavian',
  'Rustic',
  'Minimalist'
];

const BUDGET_RANGES = [
  'Under $25,000',
  '$25,000 - $50,000',
  '$50,000 - $100,000',
  '$100,000 - $250,000',
  '$250,000 - $500,000',
  '$500,000 - $1,000,000',
  'Over $1,000,000'
];

const CONTACT_ROLES = [
  'Owner',
  'Spouse/Partner',
  'Family Member',
  'Property Manager',
  'HOA Representative',
  'Contractor',
  'Other'
];

// Helper component for section headers with guidance
function SectionHeader({ icon: Icon, title, description, step }) {
  return (
    <div className="flex items-start gap-3 mb-4">
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-accent/10 text-accent flex-shrink-0">
        {step ? <span className="text-sm font-bold">{step}</span> : <Icon className="w-4 h-4" />}
      </div>
      <div>
        <h3 className="font-heading text-lg text-foreground">{title}</h3>
        {description && (
          <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
    </div>
  );
}

// Helper component for field labels with tooltips
function FieldLabel({ htmlFor, label, required, tooltip }) {
  return (
    <div className="flex items-center gap-1.5">
      <Label htmlFor={htmlFor} className="text-sm font-medium">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      {tooltip && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-sm">{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

export default function CreateProjectForm({ onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showDraftRestore, setShowDraftRestore] = useState(false);
  const DRAFT_KEY = 'new-project-draft';
  
  // Step 1: Workflow Template
  const [selectedTemplate, setSelectedTemplate] = useState('design_build_remodel');
  
  // Step 2: Project Info
  const [projectName, setProjectName] = useState('');
  const [projectType, setProjectType] = useState('');
  const [customProjectType, setCustomProjectType] = useState('');
  const [designStyle, setDesignStyle] = useState('');
  const [customDesignStyle, setCustomDesignStyle] = useState('');
  const [budgetRange, setBudgetRange] = useState('');
  const [description, setDescription] = useState('');
  
  // Step 3: Clients/Contacts (multiple)
  const [contacts, setContacts] = useState([
    { id: 1, name: '', email: '', phone: '', role: 'Owner', isPrimary: true, notes: '', is_design_decision_maker: false, is_finance_decision_maker: false }
  ]);
  
  // Step 4: Addresses
  const [clientAddress, setClientAddress] = useState({
    street: '',
    city: '',
    state: '',
    zip: ''
  });
  const [jobSiteAddress, setJobSiteAddress] = useState({
    street: '',
    city: '',
    state: '',
    zip: ''
  });
  const [sameAsClientAddress, setSameAsClientAddress] = useState(false);
  
  // Dynamic lists state (will load from backend in future)
  const [projectTypes, setProjectTypes] = useState(DEFAULT_PROJECT_TYPES);
  const [designStyles, setDesignStyles] = useState(DEFAULT_DESIGN_STYLES);
  
  // When "Same as Client" is checked, sync addresses
  useEffect(() => {
    if (sameAsClientAddress) {
      setJobSiteAddress({ ...clientAddress });
    }
  }, [sameAsClientAddress, clientAddress]);

  // Check if any data has been entered (dirty state)
  const isDirty = projectName || description || budgetRange || 
    contacts.some(c => c.name || c.email || c.phone) ||
    clientAddress.street || clientAddress.city || jobSiteAddress.street || jobSiteAddress.city;

  // Auto-save draft to localStorage on every field change
  useEffect(() => {
    if (!isDirty) return;
    const draft = {
      currentStep, selectedTemplate, projectName, projectType, customProjectType,
      designStyle, customDesignStyle, budgetRange, description, contacts,
      clientAddress, jobSiteAddress, sameAsClientAddress, savedAt: Date.now(),
    };
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(draft)); } catch {}
  }, [currentStep, selectedTemplate, projectName, projectType, customProjectType,
      designStyle, customDesignStyle, budgetRange, description, contacts,
      clientAddress, jobSiteAddress, sameAsClientAddress]);

  // On mount, check for existing draft
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const draft = JSON.parse(raw);
        if (draft.projectName || draft.contacts?.some(c => c.name)) {
          setShowDraftRestore(true);
        }
      }
    } catch {}
  }, []);

  const restoreDraft = () => {
    try {
      const draft = JSON.parse(localStorage.getItem(DRAFT_KEY));
      if (draft) {
        if (draft.currentStep) setCurrentStep(draft.currentStep);
        if (draft.selectedTemplate) setSelectedTemplate(draft.selectedTemplate);
        if (draft.projectName) setProjectName(draft.projectName);
        if (draft.projectType) setProjectType(draft.projectType);
        if (draft.customProjectType) setCustomProjectType(draft.customProjectType);
        if (draft.designStyle) setDesignStyle(draft.designStyle);
        if (draft.customDesignStyle) setCustomDesignStyle(draft.customDesignStyle);
        if (draft.budgetRange) setBudgetRange(draft.budgetRange);
        if (draft.description) setDescription(draft.description);
        if (draft.contacts?.length) setContacts(draft.contacts);
        if (draft.clientAddress) setClientAddress(draft.clientAddress);
        if (draft.jobSiteAddress) setJobSiteAddress(draft.jobSiteAddress);
        if (draft.sameAsClientAddress !== undefined) setSameAsClientAddress(draft.sameAsClientAddress);
      }
    } catch {}
    setShowDraftRestore(false);
  };

  const discardDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch {}
    setShowDraftRestore(false);
  };

  const handleCancelClick = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      onCancel();
    }
  };

  const confirmDiscard = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch {}
    setShowDiscardConfirm(false);
    onCancel();
  };
  
  // Contact management functions
  const addContact = () => {
    const newId = Math.max(...contacts.map(c => c.id)) + 1;
    setContacts([...contacts, { 
      id: newId, 
      name: '', 
      email: '', 
      phone: '', 
      role: 'Family Member',
      isPrimary: false,
      notes: '',
      is_design_decision_maker: false,
      is_finance_decision_maker: false
    }]);
  };
  
  const removeContact = (id) => {
    if (contacts.length === 1) {
      toast.error('At least one contact is required');
      return;
    }
    const updated = contacts.filter(c => c.id !== id);
    // Ensure at least one primary
    if (!updated.some(c => c.isPrimary) && updated.length > 0) {
      updated[0].isPrimary = true;
    }
    setContacts(updated);
  };
  
  const updateContact = (id, field, value) => {
    setContacts(contacts.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    ));
  };
  
  const setPrimaryContact = (id) => {
    setContacts(contacts.map(c => ({
      ...c,
      isPrimary: c.id === id
    })));
  };

  // Radio-style toggle: only one contact can hold each decision-maker flag at a time.
  const setDecisionMaker = (id, field, checked) => {
    setContacts(contacts.map(c => ({
      ...c,
      [field]: checked ? c.id === id : (c.id === id ? false : c[field]),
    })));
  };

  // Validation
  const validateStep = (step) => {
    switch(step) {
      case 1:
        return true; // Template selection always valid
      case 2:
        if (!projectName.trim()) {
          toast.error('Project name is required');
          return false;
        }
        return true;
      case 3:
        const primaryContact = contacts.find(c => c.isPrimary);
        if (!primaryContact?.name.trim()) {
          toast.error('Primary contact name is required');
          return false;
        }
        return true;
      case 4:
        return true; // Addresses optional
      default:
        return true;
    }
  };
  
  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(Math.min(currentStep + 1, 4));
    }
  };
  
  const prevStep = () => {
    setCurrentStep(Math.max(currentStep - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Final validation
    if (!projectName.trim()) {
      toast.error('Project name is required');
      setCurrentStep(2);
      return;
    }
    
    const primaryContact = contacts.find(c => c.isPrimary);
    if (!primaryContact?.name.trim()) {
      toast.error('Primary contact name is required');
      setCurrentStep(3);
      return;
    }

    setLoading(true);
    try {
      // Prepare contacts data
      const contactsData = contacts.map(c => ({
        name: c.name,
        email: c.email || null,
        phone: c.phone || null,
        role: c.role,
        is_primary: c.isPrimary,
        notes: c.notes || null,
        is_design_decision_maker: !!c.is_design_decision_maker,
        is_finance_decision_maker: !!c.is_finance_decision_maker
      }));
      
      // Determine final project type and design style
      const finalProjectType = projectType === '__custom__' ? customProjectType : projectType;
      const finalDesignStyle = designStyle === '__custom__' ? customDesignStyle : designStyle;
      
      // Create project
      const response = await axios.post(`${API}/projects`, {
        name: projectName,
        // Primary contact info (for backwards compatibility)
        client_name: primaryContact.name,
        client_email: primaryContact.email || null,
        client_phone: primaryContact.phone || null,
        // All contacts
        contacts: contactsData,
        // Client address
        client_street: clientAddress.street || null,
        client_city: clientAddress.city || null,
        client_state: clientAddress.state || null,
        client_zip: clientAddress.zip || null,
        // Job site address
        site_street: jobSiteAddress.street || null,
        site_city: jobSiteAddress.city || null,
        site_state: jobSiteAddress.state || null,
        site_zip: jobSiteAddress.zip || null,
        site_same_as_client: sameAsClientAddress,
        // Project details
        project_type: finalProjectType || null,
        design_style: finalDesignStyle || null,
        budget_range: budgetRange || null,
        description: description || null
      });

      const projectId = response.data.id;
      
      // Apply workflow template if selected
      if (selectedTemplate && selectedTemplate !== '__none__') {
        try {
          await axios.post(`${API}/projects/${projectId}/apply-template/${selectedTemplate}`);
          const templateName = WORKFLOW_TEMPLATES.find(t => t.id === selectedTemplate)?.name;
          toast.success(`Project created with ${templateName} workflow!`);
        } catch (templateError) {
          console.error('Error applying template:', templateError);
          toast.success(`Project created! Code: ${response.data.project_code}`);
          toast.warning('Could not apply workflow template');
        }
      } else {
        toast.success(`Project created! Code: ${response.data.project_code}`);
      }
      
      // Auto-generate survey based on project type
      if (finalProjectType) {
        try {
          const surveyResult = await axios.post(`${API}/projects/${projectId}/surveys/auto-generate`, {
            project_type: finalProjectType
          });
          if (surveyResult.data.created) {
            toast.success('Design survey created and ready for review');
          }
        } catch (surveyError) {
          console.error('Error auto-generating survey:', surveyError);
          // Non-critical error, don't block project creation
        }
      }
      
      // Auto-create Drawing Room portal access if client email exists
      if (primaryContact?.email) {
        try {
          await axios.post(`${API}/projects/${projectId}/drawing-room/create-access`, {
            client_email: primaryContact.email,
            client_name: primaryContact.name
          });
          toast.success('Client portal created - ready to invite client');
        } catch (portalError) {
          console.error('Error creating portal:', portalError);
          // Non-critical error
        }
      }
      
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      if (onSuccess) onSuccess(response.data);
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  // Progress indicator
  const steps = [
    { num: 1, label: 'Workflow' },
    { num: 2, label: 'Project' },
    { num: 3, label: 'Contacts' },
    { num: 4, label: 'Addresses' }
  ];

  // Prevent Enter key from submitting form until we're on the last step
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && currentStep < 4) {
      e.preventDefault();
      // Move to next step instead
      if (validateStep(currentStep)) {
        setCurrentStep(Math.min(currentStep + 1, 4));
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-6 px-2">
        {steps.map((step, idx) => (
          <div key={step.num} className="flex items-center">
            <button
              type="button"
              onClick={() => setCurrentStep(step.num)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                currentStep === step.num 
                  ? 'bg-accent text-white' 
                  : currentStep > step.num
                  ? 'bg-green-100 text-green-700'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {currentStep > step.num ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <span>{step.num}</span>
              )}
              <span className="hidden sm:inline">{step.label}</span>
            </button>
            {idx < steps.length - 1 && (
              <div className={`w-8 h-0.5 mx-2 ${currentStep > step.num ? 'bg-green-300' : 'bg-muted'}`} />
            )}
          </div>
        ))}
      </div>

      {/* STEP 1: WORKFLOW TEMPLATE */}
      {currentStep === 1 && (
        <div className="space-y-4" data-testid="step-workflow">
          <SectionHeader 
            icon={Layers} 
            step="1"
            title="Select Workflow Template" 
            description="Choose a workflow that matches your project type. This sets up phases, steps, and document requirements automatically."
          />
          
          <div className="grid grid-cols-1 gap-3">
            {WORKFLOW_TEMPLATES.map(template => (
              <button
                key={template.id}
                type="button"
                onClick={() => setSelectedTemplate(template.id)}
                className={`p-4 rounded-lg border text-left transition-all ${
                  selectedTemplate === template.id
                    ? 'border-accent bg-accent/5 ring-2 ring-accent'
                    : 'border-border hover:border-accent/50 hover:bg-muted/50'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{template.name}</span>
                      {template.recommended && (
                        <Badge className="bg-accent/10 text-accent border-0">Recommended</Badge>
                      )}
                      {template.stub && (
                        <Badge variant="outline" className="text-xs text-amber-700 border-amber-300">Stub — coming soon</Badge>
                      )}
                      {template.phases > 0 && (
                        <Badge variant="outline" className="text-xs">{template.phases} phase{template.phases === 1 ? '' : 's'}</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
                    {template.recommendedFor && (
                      <p className="text-xs text-muted-foreground/70 mt-1.5">
                        Recommended for: {template.recommendedFor}
                      </p>
                    )}
                  </div>
                  {selectedTemplate === template.id && (
                    <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  )}
                </div>
              </button>
            ))}
          </div>
          
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex gap-3">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">What happens when you select a template?</p>
              <p className="mt-1 text-blue-700">
                The workflow template automatically creates phases (like Client Onboarding, PreDesign, Schematic Design) 
                with all the steps and checklists needed to complete your project successfully.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* STEP 2: PROJECT INFORMATION */}
      {currentStep === 2 && (
        <div className="space-y-6" data-testid="step-project">
          <SectionHeader 
            icon={FileText} 
            step="2"
            title="Project Information" 
            description="Basic details about the project. These help identify and categorize the work."
          />
          
          {/* Project Name - Most Important */}
          <div className="space-y-2">
            <FieldLabel 
              htmlFor="project-name" 
              label="Project Name" 
              required 
              tooltip="A descriptive name for this project. Often includes client name and project type (e.g., 'Smith Kitchen Renovation')"
            />
            <Input
              id="project-name"
              data-testid="project-name-input"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="e.g., Turnbull Kitchen Renovation"
              className="text-base"
              required
            />
          </div>
          
          {/* Project Type & Design Style - Side by Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <FieldLabel 
                htmlFor="project-type" 
                label="Project Type" 
                tooltip="The category of work being performed"
              />
              <Select value={projectType} onValueChange={setProjectType}>
                <SelectTrigger id="project-type">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {projectTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                  <SelectItem value="__custom__">+ Add Custom Type...</SelectItem>
                </SelectContent>
              </Select>
              {projectType === '__custom__' && (
                <Input
                  value={customProjectType}
                  onChange={(e) => setCustomProjectType(e.target.value)}
                  placeholder="Enter custom project type..."
                  className="mt-2"
                />
              )}
            </div>
            
            <div className="space-y-2">
              <FieldLabel 
                htmlFor="design-style" 
                label="Design Style" 
                tooltip="The aesthetic direction for the project"
              />
              <Select value={designStyle} onValueChange={setDesignStyle}>
                <SelectTrigger id="design-style">
                  <SelectValue placeholder="Select style..." />
                </SelectTrigger>
                <SelectContent>
                  {designStyles.map(style => (
                    <SelectItem key={style} value={style}>{style}</SelectItem>
                  ))}
                  <SelectItem value="__custom__">+ Add Custom Style...</SelectItem>
                </SelectContent>
              </Select>
              {designStyle === '__custom__' && (
                <Input
                  value={customDesignStyle}
                  onChange={(e) => setCustomDesignStyle(e.target.value)}
                  placeholder="Enter custom design style..."
                  className="mt-2"
                />
              )}
            </div>
          </div>
          
          {/* Budget Range */}
          <div className="space-y-2">
            <FieldLabel 
              htmlFor="budget-range" 
              label="Budget Range" 
              tooltip="Estimated project budget. This helps with planning and resource allocation."
            />
            <Select value={budgetRange} onValueChange={setBudgetRange}>
              <SelectTrigger id="budget-range">
                <SelectValue placeholder="Select budget range..." />
              </SelectTrigger>
              <SelectContent>
                {BUDGET_RANGES.map(range => (
                  <SelectItem key={range} value={range}>{range}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Description */}
          <div className="space-y-2">
            <FieldLabel 
              htmlFor="description" 
              label="Project Description" 
              tooltip="Brief overview of the project scope and goals"
            />
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the project scope, goals, and any important notes..."
              rows={3}
            />
          </div>
        </div>
      )}

      {/* STEP 3: CONTACTS */}
      {currentStep === 3 && (
        <div className="space-y-6" data-testid="step-contacts">
          <SectionHeader 
            icon={Users} 
            step="3"
            title="Client Contacts" 
            description="Add all people involved in the project. The primary contact will be used for main communications."
          />
          
          <div className="space-y-4">
            {contacts.map((contact, idx) => (
              <div 
                key={contact.id} 
                className={`p-4 rounded-lg border ${
                  contact.isPrimary ? 'border-accent bg-accent/5' : 'border-border'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {contact.isPrimary ? (
                      <Badge className="bg-accent text-white">Primary Contact</Badge>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPrimaryContact(contact.id)}
                        className="text-xs text-muted-foreground hover:text-accent underline"
                      >
                        Set as Primary
                      </button>
                    )}
                  </div>
                  {contacts.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeContact(contact.id)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <FieldLabel 
                      htmlFor={`contact-name-${contact.id}`} 
                      label="Name" 
                      required={contact.isPrimary}
                    />
                    <Input
                      id={`contact-name-${contact.id}`}
                      value={contact.name}
                      onChange={(e) => updateContact(contact.id, 'name', e.target.value)}
                      placeholder="Full name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <FieldLabel htmlFor={`contact-role-${contact.id}`} label="Role" />
                    <Select 
                      value={contact.role} 
                      onValueChange={(v) => updateContact(contact.id, 'role', v)}
                    >
                      <SelectTrigger id={`contact-role-${contact.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONTACT_ROLES.map(role => (
                          <SelectItem key={role} value={role}>{role}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <FieldLabel htmlFor={`contact-email-${contact.id}`} label="Email" />
                    <Input
                      id={`contact-email-${contact.id}`}
                      type="email"
                      value={contact.email}
                      onChange={(e) => updateContact(contact.id, 'email', e.target.value)}
                      placeholder="email@example.com"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <FieldLabel htmlFor={`contact-phone-${contact.id}`} label="Phone" />
                    <Input
                      id={`contact-phone-${contact.id}`}
                      type="tel"
                      value={contact.phone}
                      onChange={(e) => updateContact(contact.id, 'phone', e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2 mt-4">
                  <FieldLabel htmlFor={`contact-notes-${contact.id}`} label="Contact Notes" />
                  <Input
                    id={`contact-notes-${contact.id}`}
                    data-testid={`contact-notes-${contact.id}`}
                    value={contact.notes || ''}
                    onChange={(e) => updateContact(contact.id, 'notes', e.target.value)}
                    placeholder="Preferred contact method, availability, special notes..."
                  />
                </div>

                {/* Decision-maker flags (radio-style: only one per flag across contacts) */}
                <div className="flex flex-col sm:flex-row gap-x-6 gap-y-2 mt-3">
                  <label
                    htmlFor={`contact-design-dm-${contact.id}`}
                    className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none"
                  >
                    <Checkbox
                      id={`contact-design-dm-${contact.id}`}
                      data-testid={`contact-design-dm-${contact.id}`}
                      checked={!!contact.is_design_decision_maker}
                      onCheckedChange={(v) => setDecisionMaker(contact.id, 'is_design_decision_maker', !!v)}
                    />
                    Primary design decision maker
                  </label>
                  <label
                    htmlFor={`contact-finance-dm-${contact.id}`}
                    className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none"
                  >
                    <Checkbox
                      id={`contact-finance-dm-${contact.id}`}
                      data-testid={`contact-finance-dm-${contact.id}`}
                      checked={!!contact.is_finance_decision_maker}
                      onCheckedChange={(v) => setDecisionMaker(contact.id, 'is_finance_decision_maker', !!v)}
                    />
                    Primary finance decision maker
                  </label>
                </div>
              </div>
            ))}
            
            <Button
              type="button"
              variant="outline"
              onClick={addContact}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Another Contact
            </Button>
          </div>
          
          <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 flex gap-3">
            <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Who should be listed?</p>
              <p className="mt-1 text-amber-700">
                Include all decision-makers and key stakeholders: property owners, spouses/partners, 
                HOA representatives, or anyone who needs to approve designs or be kept informed.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* STEP 4: ADDRESSES */}
      {currentStep === 4 && (
        <div className="space-y-6" data-testid="step-addresses">
          <SectionHeader 
            icon={MapPin} 
            step="4"
            title="Addresses" 
            description="Client mailing address and job site location. These may be different for investment properties or second homes."
          />
          
          {/* Client Address */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Home className="w-4 h-4 text-muted-foreground" />
              <h4 className="font-medium">Client Address</h4>
              <span className="text-xs text-muted-foreground">(Billing/Mailing)</span>
            </div>
            
            <div className="grid grid-cols-1 gap-4 pl-6">
              <div className="space-y-2">
                <FieldLabel htmlFor="client-street" label="Street Address" />
                <Input
                  id="client-street"
                  value={clientAddress.street}
                  onChange={(e) => setClientAddress({ ...clientAddress, street: e.target.value })}
                  placeholder="123 Main Street"
                />
              </div>
              
              <div className="grid grid-cols-6 gap-4">
                <div className="col-span-3 space-y-2">
                  <FieldLabel htmlFor="client-city" label="City" />
                  <Input
                    id="client-city"
                    value={clientAddress.city}
                    onChange={(e) => setClientAddress({ ...clientAddress, city: e.target.value })}
                    placeholder="City"
                  />
                </div>
                <div className="col-span-1 space-y-2">
                  <FieldLabel htmlFor="client-state" label="State" />
                  <Input
                    id="client-state"
                    value={clientAddress.state}
                    onChange={(e) => setClientAddress({ ...clientAddress, state: e.target.value })}
                    placeholder="NC"
                    maxLength={2}
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <FieldLabel htmlFor="client-zip" label="ZIP Code" />
                  <Input
                    id="client-zip"
                    value={clientAddress.zip}
                    onChange={(e) => setClientAddress({ ...clientAddress, zip: e.target.value })}
                    placeholder="28801"
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Same as Client Checkbox */}
          <div className="flex items-center gap-3 py-2 px-4 bg-muted/50 rounded-lg">
            <Checkbox
              id="same-address"
              checked={sameAsClientAddress}
              onCheckedChange={setSameAsClientAddress}
            />
            <label 
              htmlFor="same-address" 
              className="text-sm font-medium cursor-pointer select-none"
            >
              Job site is the same as client address
            </label>
          </div>
          
          {/* Job Site Address */}
          <div className={`space-y-4 transition-opacity ${sameAsClientAddress ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-muted-foreground" />
              <h4 className="font-medium">Job Site Address</h4>
              <span className="text-xs text-muted-foreground">(Where work will be performed)</span>
            </div>
            
            <div className="grid grid-cols-1 gap-4 pl-6">
              <div className="space-y-2">
                <FieldLabel htmlFor="site-street" label="Street Address" />
                <Input
                  id="site-street"
                  value={jobSiteAddress.street}
                  onChange={(e) => setJobSiteAddress({ ...jobSiteAddress, street: e.target.value })}
                  placeholder="456 Project Lane"
                  disabled={sameAsClientAddress}
                />
              </div>
              
              <div className="grid grid-cols-6 gap-4">
                <div className="col-span-3 space-y-2">
                  <FieldLabel htmlFor="site-city" label="City" />
                  <Input
                    id="site-city"
                    value={jobSiteAddress.city}
                    onChange={(e) => setJobSiteAddress({ ...jobSiteAddress, city: e.target.value })}
                    placeholder="City"
                    disabled={sameAsClientAddress}
                  />
                </div>
                <div className="col-span-1 space-y-2">
                  <FieldLabel htmlFor="site-state" label="State" />
                  <Input
                    id="site-state"
                    value={jobSiteAddress.state}
                    onChange={(e) => setJobSiteAddress({ ...jobSiteAddress, state: e.target.value })}
                    placeholder="NC"
                    maxLength={2}
                    disabled={sameAsClientAddress}
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <FieldLabel htmlFor="site-zip" label="ZIP Code" />
                  <Input
                    id="site-zip"
                    value={jobSiteAddress.zip}
                    onChange={(e) => setJobSiteAddress({ ...jobSiteAddress, zip: e.target.value })}
                    placeholder="28801"
                    disabled={sameAsClientAddress}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation & Submit Buttons */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div>
          {currentStep > 1 && (
            <Button type="button" variant="ghost" onClick={prevStep}>
              <ChevronUp className="w-4 h-4 mr-2 rotate-[-90deg]" />
              Back
            </Button>
          )}
        </div>
        
        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={handleCancelClick} data-testid="cancel-create-project">
            Cancel
          </Button>
          
          {currentStep < 4 ? (
            <Button 
              type="button" 
              onClick={(e) => { 
                e.preventDefault(); 
                e.stopPropagation();
                nextStep(); 
              }}
            >
              Continue
              <ChevronDown className="w-4 h-4 ml-2 rotate-[-90deg]" />
            </Button>
          ) : (
            <Button type="submit" disabled={loading} data-testid="create-project-btn">
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Create Project
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Discard confirmation overlay */}
      {showDiscardConfirm && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, borderRadius: 'inherit' }}>
          <div className="bg-card border border-border rounded-lg p-6 max-w-sm mx-4 shadow-lg" data-testid="discard-confirm-dialog">
            <p className="text-sm font-medium mb-1">Are you sure you want to cancel?</p>
            <p className="text-sm text-muted-foreground mb-4">Your project information will be lost.</p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setShowDiscardConfirm(false)} data-testid="keep-editing-btn">Keep Editing</Button>
              <Button type="button" variant="destructive" size="sm" onClick={confirmDiscard} data-testid="discard-btn">Discard</Button>
            </div>
          </div>
        </div>
      )}

      {/* Draft restore overlay */}
      {showDraftRestore && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, borderRadius: 'inherit' }}>
          <div className="bg-card border border-border rounded-lg p-6 max-w-sm mx-4 shadow-lg" data-testid="draft-restore-dialog">
            <p className="text-sm font-medium mb-1">Unsaved project draft found</p>
            <p className="text-sm text-muted-foreground mb-4">You have an unsaved project draft. Continue where you left off?</p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" onClick={discardDraft} data-testid="start-fresh-btn">Start Fresh</Button>
              <Button type="button" size="sm" onClick={restoreDraft} data-testid="restore-draft-btn">Restore Draft</Button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
